'use client';

import { c as _c } from "react/compiler-runtime";
import type { CompareHeaderCtaItemType } from '@/app/lcv/components/sections/compare/list/header-cta';
import { PRODUCT_COMPARE_DATA_GA, PRODUCT_COMPARE_DATA_TEST } from '@/app/lcv/components/sections/compare/tracking';
import { getCategoryNameTh, getModelColors } from '@/app/lcv/components/sections/compare/utils';
import { getSpartanCustomizeColorSwatchBackground } from '@/app/lcv/components/sections/spartan/products/customize/configurator/color-list/utils';
import { getSpartanCustomizeColorFallbackLabel } from '@/app/lcv/components/sections/spartan/products/customize/constants';
import { SPARTAN_PRODUCT_OUTLINE_NONE_CLASS_NAME } from '@/app/lcv/components/sections/spartan/products/shared/icon-button';
import { InteriorColorSwatchFace } from '@/app/lcv/components/shared/color-list/interior-color-swatch-face';
import { type ColorListItem, getLcvColorHex, getLcvColorId, getLcvColorNameTh } from '@/app/lcv/components/shared/color-list/utils';
import { SPARTAN_MU_X_RS_EXTERIOR_COLOR_TWO_TONE, SPARTAN_PRODUCT_CUSTOMIZE_INTERIOR_COLOR_MOCK, SPARTAN_PRODUCT_CUSTOMIZE_LABELS } from '@/app/lcv/contents/spartan/products/shared/customize-common-fields.data';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { formatPrice } from '@/app/utils/format';
import { GRADIENT_COLORS } from '@/services/contentstack/models/lcv-color';
import type { LcvInteriorColor, LcvModelColor } from '@/types/tis-cms/types';
interface CompareListColorPickerProps extends Omit<CompareHeaderCtaItemType, 'model'> {
  model: CompareHeaderCtaItemType['model'] | {
    colors: ColorListItem[];
    name: string;
    subCategory?: {
      title?: string | null;
    } | null;
  };
  visible?: boolean;
  isInteriorView?: boolean;
  onSelectColor: (color: LcvModelColor | null) => void;
  containerClassName?: string;
  className?: string;
  /** `sm` = 40px (compare default), `md` = 48px (calculator / customize). */
  chipSize?: 'sm' | 'md';
}
const chipSizeClassName = {
  sm: 'size-10',
  md: 'size-12'
} as const;

/** Reset Accordion `where-ul:list-ul` bullets/padding that squash chips into tall pills. */
const chipListClassName = 'flex flex-wrap items-center justify-center gap-3 space-y-0 [&>li]:pl-0 [&>li]:before:hidden';
const getModelInteriorColor = (model: CompareListColorPickerProps['model']): LcvInteriorColor | null | undefined => 'interior_color' in model ? model.interior_color : null;
const getModelExteriorTwoToneSolidColor = (model: CompareListColorPickerProps['model']) => {
  if (!('subCategory' in model)) return undefined;
  const subCategoryTitle = model.subCategory?.title?.trim().toLowerCase();
  const targetTitle = SPARTAN_MU_X_RS_EXTERIOR_COLOR_TWO_TONE.subCategoryTitle.trim().toLowerCase();
  return subCategoryTitle && subCategoryTitle === targetTitle ? SPARTAN_MU_X_RS_EXTERIOR_COLOR_TWO_TONE.solidColor : undefined;
};
const getCompareColorHex = (color: ColorListItem) => {
  const colorId = getLcvColorId(color);
  return getLcvColorHex(color) || GRADIENT_COLORS[colorId as keyof typeof GRADIENT_COLORS] || '';
};
const chipButtonClassName = (isSelected: boolean) => cn('block size-full overflow-hidden rounded-md border-2 p-0.5 transition-[border-color] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] focus-visible:outline-2 focus-visible:outline-offset-2 motion-reduce:transition-none', SPARTAN_PRODUCT_OUTLINE_NONE_CLASS_NAME, 'focus-visible:outline-gray-800', isSelected ? 'cursor-default border-gray-800' : 'cursor-pointer border-transparent hover:border-gray-800 focus-visible:border-gray-800');
export const CompareListColorPicker = t0 => {
  const $ = _c(46);
  let category;
  let className;
  let containerClassName;
  let model;
  let onSelectColor;
  let rest;
  let selectedColor;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      selectedColor,
      model,
      category,
      onSelectColor,
      visible: t1,
      isInteriorView: t2,
      containerClassName,
      className,
      chipSize: t3,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = category;
    $[2] = className;
    $[3] = containerClassName;
    $[4] = model;
    $[5] = onSelectColor;
    $[6] = rest;
    $[7] = selectedColor;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    category = $[1];
    className = $[2];
    containerClassName = $[3];
    model = $[4];
    onSelectColor = $[5];
    rest = $[6];
    selectedColor = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
  }
  const visible = t1 === undefined ? true : t1;
  const isInteriorView = t2 === undefined ? false : t2;
  const chipSize = t3 === undefined ? "sm" : t3;
  const showExteriorColors = visible && !isInteriorView;
  let t4;
  if ($[11] !== model) {
    t4 = getModelInteriorColor(model);
    $[11] = model;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const interiorColor = t4;
  let interiorName;
  let t5;
  let t6;
  let t7;
  if ($[13] !== category || $[14] !== chipSize || $[15] !== className || $[16] !== containerClassName || $[17] !== interiorColor || $[18] !== isInteriorView || $[19] !== model || $[20] !== onSelectColor || $[21] !== rest || $[22] !== selectedColor || $[23] !== showExteriorColors) {
    const exteriorTwoToneSolidColor = getModelExteriorTwoToneSolidColor(model);
    interiorName = interiorColor?.nameTh || SPARTAN_PRODUCT_CUSTOMIZE_INTERIOR_COLOR_MOCK.name;
    let t8;
    if ($[28] !== selectedColor) {
      t8 = getLcvColorId(selectedColor);
      $[28] = selectedColor;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    const selectedColorId = t8;
    let t9;
    if ($[30] !== category) {
      t9 = getCategoryNameTh(category);
      $[30] = category;
      $[31] = t9;
    } else {
      t9 = $[31];
    }
    const trackingValue = `${t9} ${model.name}`;
    const t10 = chipSizeClassName[chipSize];
    let t11;
    if ($[32] !== t10) {
      t11 = cn(t10, "shrink-0 leading-none");
      $[32] = t10;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    const chipClassName = t11;
    if ($[34] !== containerClassName) {
      t5 = cn("flex h-full flex-col items-center justify-between gap-y-4", containerClassName);
      $[34] = containerClassName;
      $[35] = t5;
    } else {
      t5 = $[35];
    }
    t6 = rest;
    t7 = isInteriorView ? <ul className={cn(chipListClassName, className)}><li className={chipClassName}><button type="button" aria-label={interiorName} aria-pressed="true" className={cn("block size-full cursor-default overflow-hidden rounded-md border-2 p-0.5 focus-visible:outline-2 focus-visible:outline-offset-2", SPARTAN_PRODUCT_OUTLINE_NONE_CLASS_NAME, "border-gray-800 focus-visible:outline-gray-800")} {...createDataAttributes({
          test: PRODUCT_COMPARE_DATA_TEST.colorSelect
        })}><InteriorColorSwatchFace interiorColor={interiorColor} fallbackColor={SPARTAN_PRODUCT_CUSTOMIZE_INTERIOR_COLOR_MOCK.swatchColor} /></button></li></ul> : showExteriorColors && <ul className={cn(chipListClassName, className)}>{getModelColors(model).map((color, index) => {
        const hex = getCompareColorHex(color);
        if (!hex) {
          return null;
        }
        const colorName = getLcvColorNameTh(color);
        const isSelected = selectedColorId === getLcvColorId(color);
        return <li key={color.documentId} className={chipClassName}><button type="button" aria-label={colorName || getSpartanCustomizeColorFallbackLabel(index)} aria-pressed={isSelected} className={chipButtonClassName(isSelected)} onClick={() => {
            if (isSelected) {
              return;
            }
            onSelectColor(color);
          }} {...createDataAttributes({
            test: `select-color-item-${index}`
          })} {...createDataGA({
            click: true,
            eventName: PRODUCT_COMPARE_DATA_GA.main,
            text: colorName,
            value: trackingValue
          })}><span aria-hidden="true" className="block size-full rounded-sm shadow-[inset_0_0_0_1px_rgba(15,23,42,0.12)]" style={{
              background: getSpartanCustomizeColorSwatchBackground(hex, exteriorTwoToneSolidColor)
            }} /></button></li>;
      })}</ul>;
    $[13] = category;
    $[14] = chipSize;
    $[15] = className;
    $[16] = containerClassName;
    $[17] = interiorColor;
    $[18] = isInteriorView;
    $[19] = model;
    $[20] = onSelectColor;
    $[21] = rest;
    $[22] = selectedColor;
    $[23] = showExteriorColors;
    $[24] = interiorName;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
  } else {
    interiorName = $[24];
    t5 = $[25];
    t6 = $[26];
    t7 = $[27];
  }
  let t8;
  if ($[36] !== interiorName || $[37] !== isInteriorView || $[38] !== selectedColor || $[39] !== showExteriorColors) {
    t8 = isInteriorView ? <p className="text-body-2 text-center" {...createDataAttributes({
      test: PRODUCT_COMPARE_DATA_TEST.colorDetail
    })}><span className="font-noto-bold">{SPARTAN_PRODUCT_CUSTOMIZE_LABELS.interiorColorPrefix}</span>{interiorName}</p> : showExteriorColors && !!selectedColor && <p className="text-body-2 text-center" {...createDataAttributes({
      test: PRODUCT_COMPARE_DATA_TEST.colorDetail
    })}>{`${selectedColor.color?.nameTh ?? ""}${(selectedColor.price ?? 0) > 0 ? ` +${formatPrice(selectedColor.price ?? 0, "none")}` : ""}`}</p>;
    $[36] = interiorName;
    $[37] = isInteriorView;
    $[38] = selectedColor;
    $[39] = showExteriorColors;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== t5 || $[42] !== t6 || $[43] !== t7 || $[44] !== t8) {
    t9 = <div className={t5} {...t6}>{t7}{t8}</div>;
    $[41] = t5;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  return t9;
};